'use client';

import { Button } from '@connected/ui/components/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@connected/ui/components/popover';
import {
    addDays,
    differenceInCalendarDays,
    eachDayOfInterval,
    endOfDay,
    endOfMonth,
    endOfWeek,
    format,
    isSameDay,
    isSameMonth,
    isToday,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import { enGB, nl } from 'date-fns/locale';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CalendarEvent } from '@/components/event-calendar';

import {
    DraggableEvent,
    DroppableCell,
    EventGap,
    EventHeight,
    EventItem,
    getAllEventsForDay,
    sortEvents,
    useEventVisibility,

} from '@/components/event-calendar';
import { DefaultStartHour } from '@/components/event-calendar/constants';

type WeekSegment = {
    event: CalendarEvent;
    startIndex: number;
    endIndex: number;
    span: number;
    isFirstDay: boolean;
    isLastDay: boolean;
};

type PlacedWeekSegment = WeekSegment & { row: number };

type WeekLayout = {
    placedSegments: PlacedWeekSegment[];
    hiddenEventsByDay: CalendarEvent[][];
    rowCount: number;
};

const DaysPerWeek = 7;

const columnStartClasses = [
    'col-start-1',
    'col-start-2',
    'col-start-3',
    'col-start-4',
    'col-start-5',
    'col-start-6',
    'col-start-7',
];

const columnSpanClasses = [
    'col-span-1',
    'col-span-2',
    'col-span-3',
    'col-span-4',
    'col-span-5',
    'col-span-6',
    'col-span-7',
];

const rowStartClasses = [
    'row-start-1',
    'row-start-2',
    'row-start-3',
    'row-start-4',
    'row-start-5',
    'row-start-6',
    'row-start-[7]',
    'row-start-[8]',
    'row-start-[9]',
    'row-start-[10]',
];

const overlayHeightClasses: Record<number, string> = {
    1: 'h-[calc(var(--event-height)+var(--event-gap))]',
    2: 'h-[calc((var(--event-height)+var(--event-gap))*2)]',
    3: 'h-[calc((var(--event-height)+var(--event-gap))*3)]',
    4: 'h-[calc((var(--event-height)+var(--event-gap))*4)]',
    5: 'h-[calc((var(--event-height)+var(--event-gap))*5)]',
};

const overlayOffsetClassOptions = [
    { threshold: 28, className: 'top-[28px]' },
    { threshold: 32, className: 'top-[32px]' },
    { threshold: 36, className: 'top-[36px]' },
    { threshold: 40, className: 'top-[40px]' },
];

function createWeekSegments(
    weekDays: (Date | undefined)[],
    events: CalendarEvent[],
): WeekSegment[] {
    const validDays = weekDays.filter(Boolean) as Date[];
    if (validDays.length === 0)
        return [];

    const weekStart = startOfDay(validDays[0]);
    const weekEnd = endOfDay(validDays[validDays.length - 1]);

    return events.reduce<WeekSegment[]>((acc, event) => {
        const eventStart = startOfDay(new Date(event.start));
        const eventEnd = startOfDay(new Date(event.end ?? event.start));

        if (eventEnd < weekStart || eventStart > weekEnd)
            return acc;

        const clampedStart = eventStart < weekStart ? weekStart : eventStart;
        const clampedEnd = eventEnd > weekEnd ? weekEnd : eventEnd;

        const startIndex = Math.max(
            0,
            Math.min(DaysPerWeek - 1, differenceInCalendarDays(clampedStart, weekStart)),
        );
        const endIndex = Math.max(
            startIndex,
            Math.min(DaysPerWeek - 1, differenceInCalendarDays(clampedEnd, weekStart)),
        );
        const span = endIndex - startIndex + 1;

        acc.push({
            event,
            startIndex,
            endIndex,
            span,
            isFirstDay: isSameDay(clampedStart, eventStart),
            isLastDay: isSameDay(clampedEnd, eventEnd),
        });

        return acc;
    }, []);
}

function assignSegmentsToRows(
    segments: WeekSegment[],
    maxRows: number,
): PlacedWeekSegment[] {
    const rowsToUse = Math.max(1, maxRows);
    const rows: WeekSegment[][] = Array.from({ length: rowsToUse }, () => []);
    const sortedSegments = [...segments].sort((a, b) => {
        if (a.startIndex !== b.startIndex)
            return a.startIndex - b.startIndex;
        if (a.span !== b.span)
            return b.span - a.span;
        return new Date(a.event.start).getTime() - new Date(b.event.start).getTime();
    });

    const placedSegments: PlacedWeekSegment[] = [];

    sortedSegments.forEach((segment) => {
        let placed = false;

        for (let rowIndex = 0; rowIndex < rowsToUse; rowIndex++) {
            const rowSegments = rows[rowIndex];
            const lastSegment = rowSegments[rowSegments.length - 1];

            if (!lastSegment || segment.startIndex > lastSegment.endIndex) {
                rowSegments.push(segment);
                placedSegments.push({ ...segment, row: rowIndex });
                placed = true;
                break;
            }
        }

        if (!placed) {
            // No available row: leave unplaced so it shows inside "+ more"
        }
    });

    return placedSegments;
}

function buildWeekLayout(
    weekDays: (Date | undefined)[],
    events: CalendarEvent[],
    maxRows: number,
): WeekLayout {
    const segments = createWeekSegments(weekDays, events);
    const placedSegments = assignSegmentsToRows(segments, maxRows);

    const visibleEventIdsByDay = weekDays.map(() => new Set<string>());

    placedSegments.forEach((segment) => {
        for (let dayIndex = segment.startIndex; dayIndex <= segment.endIndex; dayIndex++) {
            visibleEventIdsByDay[dayIndex]?.add(segment.event.id);
        }
    });

    const hiddenEventsByDay = weekDays.map((day, dayIndex) => {
        if (!day)
            return [];
        const dayEvents = sortEvents(getAllEventsForDay(events, day));
        const visibleIds = visibleEventIdsByDay[dayIndex];

        return dayEvents.filter(event => !(visibleIds?.has(event.id)));
    });

    const rowCount = placedSegments.reduce(
        (max, segment) => Math.max(max, segment.row + 1),
        0,
    );

    return {
        placedSegments,
        hiddenEventsByDay,
        rowCount,
    };
}

type MonthViewProps = {
    currentDate: Date;
    events: CalendarEvent[];
    onEventSelect: (event: CalendarEvent) => void;
    onEventCreate: (startTime: Date) => void;
    selectedEventId?: string | null;
    onBackgroundClick?: () => void;
};

export function MonthView({
    currentDate,
    events,
    onEventSelect,
    onEventCreate,
    selectedEventId,
    onBackgroundClick,
}: MonthViewProps) {
    const { i18n } = useTranslation();
    // Map i18n language to date-fns locale. Extend as more languages are supported.
    const dateFnsLocale = useMemo(() => {
        const lang = i18n.language || 'en-GB';
        if (lang.startsWith('nl'))
            return nl;
        return enGB;
    }, [i18n.language]);
    const days = useMemo(() => {
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(monthStart);
        // Use locale to determine week start (e.g., Monday for nl, Sunday for en-US/en-GB as configured by locale)
        const calendarStart = startOfWeek(monthStart, { locale: dateFnsLocale });
        const calendarEnd = endOfWeek(monthEnd, { locale: dateFnsLocale });

        return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    }, [currentDate, dateFnsLocale]);

    const weekdays = useMemo(() => {
        return Array.from({ length: 7 }).map((_, i) => {
            const date = addDays(startOfWeek(new Date(), { locale: dateFnsLocale }), i);
            return format(date, 'EEE', { locale: dateFnsLocale });
        });
    }, [dateFnsLocale]);

    const weeks = useMemo(() => {
        const result = [];
        let week = [];

        for (let i = 0; i < days.length; i++) {
            week.push(days[i]);
            if (week.length === 7 || i === days.length - 1) {
                result.push(week);
                week = [];
            }
        }

        return result;
    }, [days]);

    const [internalActiveEventId, setInternalActiveEventId] = useState<string | null>(null);
    const activeEventId = selectedEventId ?? internalActiveEventId;

    const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
        e.stopPropagation();
        setInternalActiveEventId(event.id ?? null);
        onEventSelect(event);
    };

    const handleBackgroundClick = () => {
        setInternalActiveEventId(null);
        onBackgroundClick?.();
    };

    const { contentRef, contentHeight } = useEventVisibility({
        eventHeight: EventHeight,
        eventGap: EventGap,
    });
    const rowPixelHeight = EventHeight + EventGap;
    const fallbackRows = 4;
    const contentAreaHeight = contentHeight ?? fallbackRows * rowPixelHeight;
    const maxRowCapacity = Math.max(1, Math.floor(contentAreaHeight / rowPixelHeight));
    const weekLayouts = useMemo(
        () => weeks.map(week => buildWeekLayout(week, events, maxRowCapacity)),
        [weeks, events, maxRowCapacity],
    );
    const { t } = useTranslation('dashboard');
    const overlayOffsetValue = contentRef.current?.offsetTop ?? 32;
    const overlayOffsetClass = (
        overlayOffsetClassOptions.find(option => overlayOffsetValue <= option.threshold)
        ?? overlayOffsetClassOptions[overlayOffsetClassOptions.length - 1]
    ).className;
    const overlayHeightClass
        = overlayHeightClasses[Math.min(maxRowCapacity, 5)]
            ?? overlayHeightClasses[4];
    const renderedEventIds = new Set<string>();

    return (
        <div data-slot="month-view" className="contents">
            <div className="border-border/70 grid grid-cols-7 border-b">
                {weekdays.map(day => (
                    <div
                        key={day}
                        className="text-muted-foreground/70 py-2 text-center text-sm"
                    >
                        {day}
                    </div>
                ))}
            </div>
            <div className="grid flex-1 auto-rows-fr" onClick={handleBackgroundClick}>
                {weeks.map((week, weekIndex) => {
                    const layout = weekLayouts[weekIndex];
                    const hiddenEventsByDay = layout?.hiddenEventsByDay ?? week.map(() => []);
                    const placedSegments = layout?.placedSegments ?? [];
                    const weekKey = week[0] ? `week-${week[0].toISOString()}` : `week-${weekIndex}`;

                    return (
                        <div key={weekKey} className="relative">
                            <div className="grid grid-cols-7">
                                {week.map((day, dayIndex) => {
                                    if (!day)
                                        return null;

                                    const isCurrentMonth = isSameMonth(day, currentDate);
                                    const cellId = `month-cell-${day.toISOString()}`;
                                    const isReferenceCell = weekIndex === 0 && dayIndex === 0;
                                    const hiddenEvents = hiddenEventsByDay[dayIndex] ?? [];
                                    const hasMore = hiddenEvents.length > 0;

                                    return (
                                        <div
                                            key={day.toString()}
                                            className={`group border-border/70 data-outside-cell:bg-muted/25 data-outside-cell:text-muted-foreground/70 border-r border-b last:border-r-0 ${weekIndex === weeks.length - 1 ? 'border-b-0' : ''}`}
                                            data-today={isToday(day) || undefined}
                                            data-outside-cell={!isCurrentMonth || undefined}
                                        >
                                            <DroppableCell
                                                id={cellId}
                                                date={day}
                                                onClick={() => {
                                                    handleBackgroundClick();
                                                    const startTime = new Date(day);
                                                    startTime.setHours(DefaultStartHour, 0, 0);
                                                    onEventCreate(startTime);
                                                }}
                                            >
                                                <div className={`mt-1 inline-flex size-6 items-center justify-center rounded-full text-sm ${isToday(day) ? 'bg-orange-500 text-white' : ''}`}>
                                                    {format(day, 'd', { locale: dateFnsLocale })}
                                                </div>
                                                <div
                                                    ref={isReferenceCell ? contentRef : undefined}
                                                    className="min-h-[calc((var(--event-height)+var(--event-gap))*2)] sm:min-h-[calc((var(--event-height)+var(--event-gap))*3)] lg:min-h-[calc((var(--event-height)+var(--event-gap))*4)]"
                                                />
                                                {hasMore && (
                                                    <Popover modal>
                                                        <PopoverTrigger asChild>
                                                            <Button
                                                                className="focus-visible:border-ring !bg-primary-foreground focus-visible:ring-ring/50 text-muted-foreground hover:text-foreground hover:bg-muted/50 mt-[var(--event-gap)] flex h-[var(--event-height)] w-full items-center overflow-hidden px-1 text-left text-[10px] backdrop-blur-md transition outline-none select-none focus-visible:ring-[3px] sm:px-2 sm:text-xs"
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                <span>
                                                                    +
                                                                    {' '}
                                                                    {hiddenEvents.length}
                                                                    {' '}
                                                                    <span className="max-sm:sr-only">{t('dashboard.planning.message.moreMsgs')}</span>
                                                                </span>
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent
                                                            align="center"
                                                            className="max-w-52 p-3"
                                                            style={
                                                                {
                                                                    '--event-height': `${EventHeight}px`,
                                                                } as React.CSSProperties
                                                            }
                                                        >
                                                            <div className="space-y-2">
                                                                <div className="text-sm font-medium">
                                                                    {format(day, 'd MMM, EEEE', { locale: dateFnsLocale })}
                                                                </div>
                                                                <div className="space-y-1">
                                                                    {hiddenEvents.map((event) => {
                                                                        const eventStart = new Date(event.start);
                                                                        const eventEnd = new Date(event.end ?? event.start);
                                                                        const isFirstDay = isSameDay(day, eventStart);
                                                                        const isLastDay = isSameDay(day, eventEnd);

                                                                        return (
                                                                            <EventItem
                                                                                key={`${event.id}-${day.toISOString()}`}
                                                                                onClick={e => handleEventClick(event, e)}
                                                                                event={event}
                                                                                view="month"
                                                                                isFirstDay={isFirstDay}
                                                                                isLastDay={isLastDay}
                                                                                isSelected={event.id === activeEventId}
                                                                            />
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </PopoverContent>
                                                    </Popover>
                                                )}
                                            </DroppableCell>
                                        </div>
                                    );
                                })}
                            </div>
                            {placedSegments.length > 0 && (
                                <div
                                    className={`pointer-events-none absolute inset-x-0 grid grid-cols-7 auto-rows-[var(--event-height)] gap-x-0 gap-y-[var(--event-gap)] ${overlayOffsetClass} ${overlayHeightClass}`}
                                >
                                    {placedSegments.map((segment) => {
                                        const startClass = columnStartClasses[Math.min(segment.startIndex, columnStartClasses.length - 1)];
                                        const spanClass = columnSpanClasses[Math.min(segment.span, columnSpanClasses.length) - 1];
                                        const rowClass = rowStartClasses[Math.min(segment.row, rowStartClasses.length - 1)];
                                        const eventKey = segment.event.id || `${segment.event.title}-${segment.startIndex}-${segment.endIndex}`;
                                        const hasShownBefore = eventKey ? renderedEventIds.has(eventKey) : false;

                                        if (eventKey && !hasShownBefore) {
                                            renderedEventIds.add(eventKey);
                                        }

                                        return (
                                            <div
                                                key={`${weekKey}-${segment.event.id}-${segment.row}-${segment.startIndex}`}
                                                className={`pointer-events-auto px-0.5 ${startClass} ${spanClass} ${rowClass}`}
                                            >
                                                <div className="h-full">
                                                    <DraggableEvent
                                                        event={segment.event}
                                                        view="month"
                                                        onClick={e => handleEventClick(segment.event, e)}
                                                        isFirstDay={segment.isFirstDay}
                                                        isLastDay={segment.isLastDay}
                                                        hideContent={hasShownBefore}
                                                        isSelected={segment.event.id === activeEventId}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
